import { computed, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { HttpService } from './http-service';
import { firstValueFrom, map, take, tap } from 'rxjs';
import { Router } from '@angular/router';
import { LoginResponse } from '../dto/loginResponse';



@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private httpService: HttpService = inject(HttpService);
  private router: Router = inject(Router);
  private readonly authSubPath = '/auth';

  private nextRefreshDate: WritableSignal<Date | undefined> = signal(undefined);

  currentFamilyId = signal('');
  currentlyLoggedIn = computed(() => !!this.nextRefreshDate());
  canAccessAdmin = signal(false);

  async tryLogin(username: string, password: string): Promise<{ success: boolean, redirectTo: string }> {
    return await firstValueFrom(this.httpService.postToObs<LoginResponse>(`${this.authSubPath}/login`, JSON.stringify({ username, password })).pipe(
      take(1),
      tap((response: LoginResponse) => {
        if (response.success) {
          localStorage.setItem('jwt', response.jwt);
          let nextRefreshDate = new Date(new Date().getTime() + 19 * 60000);
          this.nextRefreshDate.set(nextRefreshDate);
          this.canAccessAdmin.set(response.canAccessAdmin);
          this.currentFamilyId.set(response.familyUuid);
        }
      }),
      map((response: LoginResponse) => { return { success: response.success, redirectTo: response.redirectTo } })
    ));
  }

  async tryPasscodeLogin(passcode: string): Promise<{ success: boolean, redirectTo: string }> {
    return await firstValueFrom(this.httpService.getToObs<LoginResponse>(`${this.authSubPath}/passcode-login/${passcode}`).pipe(
      take(1),
      tap((response: LoginResponse) => {
        if (response.success) {
          localStorage.setItem('jwt', response.jwt);
          let nextRefreshDate = new Date(new Date().getTime() + 19 * 60000);
          this.nextRefreshDate.set(nextRefreshDate);
          this.canAccessAdmin.set(response.canAccessAdmin);
          this.currentFamilyId.set(response.familyUuid);
        }
      }),
      map((response: LoginResponse) => { return { success: response.success, redirectTo: response.redirectTo } })
    ));
  }

  async isTokenValid(jwt: string): Promise<boolean> {
    return this.httpService.postToPromise<boolean>(`${this.authSubPath}/verify-jwt`, JSON.stringify({ jwt }));
  }

  async tryTokenLogin(token: string): Promise<{ success: boolean, redirectTo: string }> {
    return await firstValueFrom(this.httpService.getToObs<LoginResponse>(`${this.authSubPath}/token-login/${token}`).pipe(
      take(1),
      tap((response: LoginResponse) => {
        if (response.success) {
          localStorage.setItem('jwt', response.jwt);
          let nextRefreshDate = new Date(new Date().getTime() + 19 * 60000);
          this.nextRefreshDate.set(nextRefreshDate);
          this.canAccessAdmin.set(response.canAccessAdmin);
          this.currentFamilyId.set(response.familyUuid);
        }
      }),
      map((response: LoginResponse) => { return { success: response.success, redirectTo: response.redirectTo } })
    ));
  }

  async logOff(): Promise<boolean> {
    const jwt = localStorage.getItem('jwt');
    localStorage.removeItem('jwt');
    this.nextRefreshDate.set(undefined);
    this.canAccessAdmin.set(false);
    this.currentFamilyId.set('');
    return this.httpService.postToPromise<boolean>(`${this.authSubPath}/logOff`, JSON.stringify({ jwt }));
  }

  async refreshJwt(jwt: string): Promise<boolean> {
    return await firstValueFrom(this.httpService.postToObs<LoginResponse>(`${this.authSubPath}/refresh`, JSON.stringify({ jwt })).pipe(
      take(1),
      tap((response: LoginResponse) => {
        if (response.success) {
          localStorage.setItem('jwt', response.jwt);
          let nextRefreshDate = new Date(new Date().getTime() + 19 * 60000);
          this.nextRefreshDate.set(nextRefreshDate);
          this.canAccessAdmin.set(response.canAccessAdmin);
          this.currentFamilyId.set(response.familyUuid);
        } else {
          localStorage.removeItem('jwt');
          this.nextRefreshDate.set(undefined);
          this.canAccessAdmin.set(false);
          this.currentFamilyId.set('');
        }
      }),
      map((response: LoginResponse) => response.success)
    ));
  }

  askToRefreshIfNeeded(): void {

    if (this.nextRefreshDate()) {
      const shouldRefreshTime = this.nextRefreshDate()!.getTime();
      const now = new Date().getTime();
      const jwt = localStorage.getItem('jwt');

      if (shouldRefreshTime < now && !!jwt) {
        this.refreshJwt(jwt).then(
          success => {
            if (!success) {
              this.router.navigate([`/`]);
            }
          }
        )
      }
    }
  }
}
